import { ChangeEvent, FormEvent, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createSupabaseSyncAdapter, getSyncQueue, localGallery, localInquiries, localMessages, localNews, localPartners, localProducts, localSyncQueue, localTeamMembers, localUsers, processSyncQueue, queueSyncOperation } from '@siaedts/shared';
import { db, getCurrentUser, getPublicUrl, subscribeToContactMessages, supabase, uploadFile } from '@siaedts/shared/lib/supabase';
import type { ContactMessage, Inquiry, InquiryStatus, News, Partner, Product, ProductCategory, TeamMember, User } from '@siaedts/shared';
import { useLanguage } from '../i18n';

type Section = 'Products' | 'News' | 'Gallery' | 'Partners' | 'Team' | 'Inquiries' | 'Messages' | 'Feedback' | 'Users';
type Currency = 'USD' | 'EUR' | 'GBP' | 'CAD' | 'AUD' | 'DZD' | 'MAD' | 'XOF' | 'GNF';
type ProductForm = { name: string; slug: string; category_id: string; price: number | null; currency: Currency; description: string; short_description: string; status: Product['status']; featured: boolean; image_url: string | null; gallery_images: string[]; features: string[]; specifications: Record<string, string>; included_items: string[]; sort_order: number; meta_title: string | null; meta_description: string | null };
type NewsForm = { title: string; slug: string; content: string; excerpt: string | null; category: 'company' | 'industry' | 'launches' | 'events' | 'tips'; featured_image_url: string | null; gallery_images: string[]; published_at: string | null; status: News['status']; featured: boolean; meta_title: string | null; meta_description: string | null };
type GalleryForm = { title: string; description: string | null; image_url: string; category: string | null; sort_order: number; is_published: boolean };
type PartnerForm = { name: string; description: string | null; logo_url: string | null; website_url: string | null; type: Partner['type']; is_active: boolean; sort_order: number };
type TeamForm = { name: string; role: string; bio: string | null; image_url: string | null; email: string | null; phone: string | null; linkedin_url: string | null; sort_order: number; is_active: boolean };
type UserForm = { email: string; name: string; role: User['role']; password?: string };

const emptyProduct: ProductForm = { name: '', slug: '', category_id: '', price: null, currency: 'USD', description: '', short_description: '', status: 'draft', featured: false, image_url: null, gallery_images: [], features: [], specifications: {}, included_items: [], sort_order: 0, meta_title: null, meta_description: null };
const emptyNews: NewsForm = { title: '', slug: '', content: '', excerpt: null, category: 'company', featured_image_url: null, gallery_images: [], published_at: null, status: 'draft', featured: false, meta_title: null, meta_description: null };
const emptyGallery: GalleryForm = { title: '', description: null, image_url: '', category: '', sort_order: 0, is_published: true };
const emptyPartner: PartnerForm = { name: '', description: null, logo_url: null, website_url: null, type: 'national', is_active: true, sort_order: 0 };
const emptyTeam: TeamForm = { name: '', role: '', bio: null, image_url: null, email: null, phone: null, linkedin_url: null, sort_order: 0, is_active: true };
const emptyUser: UserForm = { email: '', name: '', role: 'viewer', password: '' };

const repositories = { Gallery: localGallery, Partners: localPartners, Team: localTeamMembers, Inquiries: localInquiries, Messages: localMessages, Feedback: localMessages, Users: localUsers };

function slugify(value: string) {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

// Unique suffix avoids collisions on the UNIQUE slug column.
function generateSlug(value: string, fallback: string) {
  return `${slugify(value).slice(0, 80) || fallback}-${crypto.randomUUID().slice(0, 6)}`;
}

function describeError(error: unknown, fallback: string) {
  if (error instanceof Error) return error.message;
  if (error && typeof error === 'object') {
    const structured = error as { message?: string; details?: string; hint?: string; code?: string };
    const parts = [structured.code, structured.message, structured.details, structured.hint].filter(Boolean);
    if (parts.length > 0) return parts.join(' - ');
  }
  return fallback;
}

function groupMessageRows(rows: Array<Record<string, unknown>>): Array<Record<string, unknown>> {
  const groups = new Map<string, Array<Record<string, unknown>>>();
  const standalone = rows.filter((row) => !row.conversation_id);
  const conversationRows = rows.filter((row) => row.conversation_id);

  for (const row of conversationRows) {
    const key = String(row.conversation_id);
    const messages = groups.get(key) ?? [];
    messages.push(row);
    groups.set(key, messages);
  }

  const grouped: Array<Record<string, unknown>> = Array.from(groups.values()).map((messages) => {
    const ordered = [...messages].sort((left, right) => String(left.created_at ?? '').localeCompare(String(right.created_at ?? '')));
    const latest = ordered[ordered.length - 1];
    return {
      ...latest,
      conversation_messages: ordered,
      conversation_count: ordered.length,
      status: ordered.some((message) => message.status === 'unread') ? 'unread' : latest.status,
    };
  });

  return [...grouped, ...standalone].sort((left, right) => String(right.created_at ?? '').localeCompare(String(left.created_at ?? '')));
}

function flattenMessageRows(rows: Array<Record<string, unknown>>) {
  return rows.flatMap((row) => Array.isArray(row.conversation_messages) ? row.conversation_messages as Array<Record<string, unknown>> : [row]);
}

export function AdminCopy({ children }: { children: ReactNode }) {
  const { language, text } = useLanguage();
  const root = useRef<HTMLDivElement>(null);
  const originalText = useRef(new WeakMap<Text, { original: string; translated: string }>());
  const originalAttributes = useRef(new WeakMap<Element, Map<string, { original: string; translated: string }>>());

  useLayoutEffect(() => {
    const container = root.current;
    if (!container) return;

    const translateTree = (node: Node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const textNode = node as Text;
        if (textNode.parentElement?.closest('td') && !textNode.parentElement.closest('button, option')) return;
        const current = textNode.textContent ?? '';
        const previous = originalText.current.get(textNode);
        const original = previous && current === previous.translated ? previous.original : current;
        const trimmed = original.trim();
        const translated = language === 'fr' && trimmed ? text(trimmed) : trimmed;
        const start = original.indexOf(trimmed);
        const next = language === 'fr' && translated !== trimmed
          ? `${original.slice(0, start)}${translated}${original.slice(start + trimmed.length)}`
          : original;
        originalText.current.set(textNode, { original, translated: next });
        if (current !== next) {
          textNode.textContent = next;
        }
        return;
      }
      if (node.nodeType !== Node.ELEMENT_NODE) return;

      const element = node as Element;
      const attributes = ['alt', 'aria-label', 'placeholder', 'title'];
      let saved = originalAttributes.current.get(element);
      if (!saved) {
        saved = new Map();
        originalAttributes.current.set(element, saved);
      }
      for (const attribute of attributes) {
        const current = element.getAttribute(attribute);
        if (current === null) continue;
        const previous = saved.get(attribute);
        const original = previous && current === previous.translated ? previous.original : current;
        const next = language === 'fr' ? text(original) : original;
        saved.set(attribute, { original, translated: next });
        if (current !== next) element.setAttribute(attribute, next);
      }

      for (const child of Array.from(node.childNodes)) translateTree(child);
    };

    translateTree(container);
    const observer = new MutationObserver((records) => {
      for (const record of records) {
        if (record.type === 'childList') record.addedNodes.forEach(translateTree);
        else if (record.target.parentElement) translateTree(record.target);
      }
    });
    observer.observe(container, { childList: true, characterData: true, subtree: true });
    return () => observer.disconnect();
  }, [language, text]);

  return <div className="admin-copy-scope" ref={root}>{children}</div>;
}

export default function AdminSection({ section }: { section: Section }) {
  const { language, t } = useLanguage();
  const [rows, setRows] = useState<Array<Record<string, unknown>>>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [productForm, setProductForm] = useState<ProductForm>(emptyProduct);
  const [newsForm, setNewsForm] = useState<NewsForm>(emptyNews);
  const [galleryForm, setGalleryForm] = useState<GalleryForm>(emptyGallery);
  const [partnerForm, setPartnerForm] = useState<PartnerForm>(emptyPartner);
  const [teamForm, setTeamForm] = useState<TeamForm>(emptyTeam);
  const [userForm, setUserForm] = useState<UserForm>(emptyUser);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [categories, setCategories] = useState<Array<{ id: string; name: string }>>([]);
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [selectedDetail, setSelectedDetail] = useState<Record<string, unknown> | null>(null);

  function closeDetail() {
    setSelectedDetail(null);
  }

  function updateSelectedDetailStatus(status: string) {
    setSelectedDetail((current) => current ? { ...current, status } : current);
  }

  async function loadRows() {
    const repository = section === 'Products' ? localProducts : section === 'News' ? localNews : repositories[section];
    let items = (await repository.list()) as unknown as Array<Record<string, unknown>>;

    if (navigator.onLine) {
      try {
        let remoteItems: Array<Record<string, unknown>> | null = null;
        if (section === 'Inquiries') {
          remoteItems = (await db.inquiries.getAll()) as unknown as Array<Record<string, unknown>>;
        } else if (section === 'Messages') {
          remoteItems = (await db.contactMessages.getAll()) as unknown as Array<Record<string, unknown>>;
        } else if (section === 'Feedback') {
          remoteItems = (await db.feedback.getAll()) as unknown as Array<Record<string, unknown>>;
        } else if (section === 'Team') {
          remoteItems = (await db.team.getAll()) as unknown as Array<Record<string, unknown>>;
        } else if (section === 'Partners') {
          remoteItems = (await db.partners.getAll()) as unknown as Array<Record<string, unknown>>;
        } else if (section === 'Gallery') {
          remoteItems = (await db.gallery.getAll()) as unknown as Array<Record<string, unknown>>;
        } else if (section === 'Users') {
          remoteItems = (await db.users.getAll()) as unknown as Array<Record<string, unknown>>;
        } else if (section === 'Products') {
          remoteItems = (await db.products.getAll()) as unknown as Array<Record<string, unknown>>;
        } else if (section === 'News') {
          remoteItems = (await db.news.getAll()) as unknown as Array<Record<string, unknown>>;
        }

        if (remoteItems && Array.isArray(remoteItems)) {
          for (const item of remoteItems) {
            const exists = items.some((local) => local.id === item.id);
            if (!exists) {
              await repository.create(item as never);
            }
          }
          items = remoteItems;
        }
      } catch (err) {
        console.warn('Could not load remote rows, using local cache:', err);
      }
    }

    setRows(section === 'Messages' ? groupMessageRows(items) : items);
  }

  useEffect(() => {
    void loadRows();
    setShowForm(false);
    setEditingId(null);
    setShowCategoryForm(false);
    setNewCategoryName('');
    setSelectedIds([]);
    if (section === 'Products') void loadCategories();
  }, [section]);

  useEffect(() => {
    if (section !== 'Messages') return;
    let channel: ReturnType<typeof subscribeToContactMessages> | null = null;
    const pollTimer = window.setInterval(() => { void loadRows(); }, 2500);
    try {
      channel = subscribeToContactMessages((payload) => {
        const change = payload as { eventType?: string; new?: Record<string, unknown>; old?: Record<string, unknown> };
        setRows((current) => {
          const flattened = flattenMessageRows(current);
          if (change.eventType === 'DELETE' && change.old?.id) {
            return groupMessageRows(flattened.filter((row) => row.id !== change.old?.id));
          }
          if (!change.new?.id) return current;
          const withoutChanged = flattened.filter((row) => row.id !== change.new?.id);
          return groupMessageRows([change.new, ...withoutChanged]);
        });
      });
    } catch {
      channel = null;
    }
    return () => {
      window.clearInterval(pollTimer);
      if (channel) void supabase.removeChannel(channel);
    };
  }, [section]);

  async function loadCategories() {
    setCategoriesLoading(true);
    try {
      const existing = await db.categories.getAll() as unknown as ProductCategory[];
      if (existing.length > 0) {
        const nextCategories = existing.map((category) => ({ id: category.id, name: category.name }));
        setCategories(nextCategories);
        setProductForm((current) => ({
          ...current,
          category_id: nextCategories.some((category) => category.id === current.category_id)
            ? current.category_id
            : nextCategories[0].id,
        }));
        return;
      }

      if (!navigator.onLine) {
        setMessage('No product categories are available yet. Connect to the Internet to prepare the category list.');
        return;
      }

      const created = await db.categories.create({ name: 'General Agriculture', slug: 'general-agriculture', description: 'General agricultural products', sort_order: 0 }) as unknown as ProductCategory;
      setCategories([{ id: created.id, name: created.name }]);
      setProductForm((current) => ({ ...current, category_id: created.id }));
    } catch (error) {
      setMessage(`Could not load product categories: ${describeError(error, 'Unknown Supabase error')}`);
    } finally {
      setCategoriesLoading(false);
    }
  }

  async function createCategory(event: FormEvent) {
    event.preventDefault();
    const name = newCategoryName.trim();
    if (!name) return;
    setBusy(true);
    setMessage('Creating category...');
    try {
      if (!navigator.onLine) throw new Error('Reconnect to create a product category.');
      const created = await db.categories.create({
        name,
        slug: slugify(name),
        description: null,
        sort_order: categories.length,
      }) as unknown as ProductCategory;
      const nextCategory = { id: created.id, name: created.name };
      setCategories((current) => [...current, nextCategory]);
      setProductForm((current) => ({ ...current, category_id: created.id }));
      setNewCategoryName('');
      setShowCategoryForm(false);
      setMessage(`Category "${created.name}" added and selected.`);
    } catch (error) {
      setMessage(describeError(error, 'Could not create category.'));
    } finally {
      setBusy(false);
    }
  }

  async function deleteCategory(category: { id: string; name: string }) {
    if (!window.confirm(`Delete the category "${category.name}"? Products using it must be reassigned first.`)) return;
    setBusy(true);
    setMessage(`Deleting category "${category.name}"...`);
    try {
      if (!navigator.onLine) throw new Error('Reconnect to delete a product category.');
      await db.categories.delete(category.id);
      setCategories((current) => current.filter((item) => item.id !== category.id));
      if (productForm.category_id === category.id) {
        setProductForm((current) => ({ ...current, category_id: categories.find((item) => item.id !== category.id)?.id ?? '' }));
      }
      setMessage(`Category "${category.name}" deleted.`);
    } catch (error) {
      setMessage(`Could not delete category: ${describeError(error, 'It may still be assigned to products.')}`);
    } finally {
      setBusy(false);
    }
  }

  function beginCreate() {
    setEditingId(null);
    setProductForm({ ...emptyProduct, category_id: categories[0]?.id ?? '' });
    setNewsForm(emptyNews);
    setGalleryForm(emptyGallery);
    setPartnerForm(emptyPartner);
    setTeamForm(emptyTeam);
    setUserForm(emptyUser);
    setMessage('');
    setShowForm(true);
  }

  function beginEdit(row: Record<string, unknown>) {
    setEditingId(String(row.id));
    if (section === 'Products') {
      const savedCategoryId = String(row.category_id ?? '');
      setProductForm({ name: String(row.name ?? ''), slug: String(row.slug ?? ''), category_id: categories.some((category) => category.id === savedCategoryId) ? savedCategoryId : categories[0]?.id ?? '', price: typeof row.price === 'number' ? row.price : null, currency: (row.currency as Currency) || 'USD', description: String(row.description ?? ''), short_description: String(row.short_description ?? ''), status: row.status as Product['status'], featured: Boolean(row.featured), image_url: row.image_url ? String(row.image_url) : null, gallery_images: Array.isArray(row.gallery_images) ? row.gallery_images as string[] : [], features: Array.isArray(row.features) ? row.features as string[] : [], specifications: row.specifications && typeof row.specifications === 'object' ? row.specifications as Record<string, string> : {}, included_items: Array.isArray(row.included_items) ? row.included_items as string[] : [], sort_order: Number(row.sort_order ?? 0), meta_title: row.meta_title ? String(row.meta_title) : null, meta_description: row.meta_description ? String(row.meta_description) : null });
    } else if (section === 'News') {
      setNewsForm({ title: String(row.title ?? ''), slug: String(row.slug ?? ''), content: String(row.content ?? ''), excerpt: row.excerpt ? String(row.excerpt) : null, category: ['company', 'industry', 'launches', 'events', 'tips'].includes(String(row.category)) ? String(row.category) as NewsForm['category'] : 'company', featured_image_url: row.featured_image_url ? String(row.featured_image_url) : null, gallery_images: Array.isArray(row.gallery_images) ? row.gallery_images as string[] : [], published_at: row.published_at ? String(row.published_at).slice(0, 10) : null, status: row.status as News['status'], featured: Boolean(row.featured), meta_title: row.meta_title ? String(row.meta_title) : null, meta_description: row.meta_description ? String(row.meta_description) : null });
    } else if (section === 'Gallery') {
      setGalleryForm({ title: String(row.title ?? ''), description: row.description ? String(row.description) : null, image_url: row.image_url ? String(row.image_url) : '', category: row.category ? String(row.category) : null, sort_order: Number(row.sort_order ?? 0), is_published: Boolean(row.is_published) });
    } else if (section === 'Partners') {
      setPartnerForm({ name: String(row.name ?? ''), description: row.description ? String(row.description) : null, logo_url: row.logo_url ? String(row.logo_url) : null, website_url: row.website_url ? String(row.website_url) : null, type: row.type === 'international' ? 'international' : 'national', is_active: Boolean(row.is_active), sort_order: Number(row.sort_order ?? 0) });
    } else if (section === 'Team') {
      setTeamForm({ name: String(row.name ?? ''), role: String(row.role ?? ''), bio: row.bio ? String(row.bio) : null, image_url: row.image_url ? String(row.image_url) : null, email: row.email ? String(row.email) : null, phone: row.phone ? String(row.phone) : null, linkedin_url: row.linkedin_url ? String(row.linkedin_url) : null, sort_order: Number(row.sort_order ?? 0), is_active: Boolean(row.is_active) });
    } else if (section === 'Users') {
      setUserForm({ email: String(row.email ?? ''), name: String(row.name ?? ''), role: (row.role as User['role']) || 'viewer', password: '' });
    }
    setMessage('');
    setShowForm(true);
  }

  async function handleImageUpload(event: ChangeEvent<HTMLInputElement>, kind: 'product' | 'news' | 'gallery' | 'partner' | 'team', gallery = false) {
    const files = Array.from(event.target.files ?? []);
    if (files.length === 0) return;
    setBusy(true); setMessage('Uploading image...');
    try {
      if (!navigator.onLine) throw new Error('Image upload requires a connection. Save the content first and upload when online.');
      const bucket = kind === 'product' ? 'product-images' : kind === 'news' ? 'news-images' : kind === 'gallery' ? 'gallery-images' : kind === 'partner' ? 'partner-logos' : 'team-images';
      const urls: string[] = [];
      for (const file of files) {
        const path = `${kind}/${crypto.randomUUID()}-${file.name.replace(/[^a-zA-Z0-9._-]/g, '-')}`;
        await uploadFile(bucket, path, file);
        urls.push(getPublicUrl(bucket, path));
      }
      if (kind === 'product') setProductForm((current) => gallery ? ({ ...current, gallery_images: [...current.gallery_images, ...urls] }) : ({ ...current, image_url: urls[0] }));
      else if (kind === 'news') setNewsForm((current) => gallery ? ({ ...current, gallery_images: [...current.gallery_images, ...urls] }) : ({ ...current, featured_image_url: urls[0] }));
      else if (kind === 'gallery') setGalleryForm((current) => ({ ...current, image_url: urls[0], thumbnail_url: urls[0] }));
      else if (kind === 'partner') setPartnerForm((current) => ({ ...current, logo_url: urls[0] }));
      else setTeamForm((current) => ({ ...current, image_url: urls[0] }));
      setMessage(`${urls.length} image${urls.length === 1 ? '' : 's'} uploaded. Save the record to publish the change.`);
    } catch (error) {
      const detail = error instanceof Error ? error.message : 'Image upload failed.';
      setMessage(detail.toLowerCase().includes('bucket not found') ? 'Storage bucket is missing. Apply the latest Supabase migration (20260910000002_storage_buckets.sql), then try again.' : detail);
    }
    finally { setBusy(false); event.target.value = ''; }
  }

  async function saveProduct(event: FormEvent) {
    event.preventDefault(); setBusy(true); setMessage('');
    try {
      const payload = { ...productForm, slug: editingId && productForm.slug ? productForm.slug : generateSlug(productForm.name, 'product'), price: productForm.price, currency: productForm.currency, description: productForm.description || null, short_description: productForm.short_description || null, image_url: productForm.image_url, meta_title: productForm.meta_title || null, meta_description: productForm.meta_description || null };
      if (navigator.onLine) {
        if (editingId) {
          await db.products.update(editingId, payload);
          await localProducts.update(editingId, payload);
        } else {
          const remoteRecord = await db.products.create(payload) as unknown as { id: string };
          await localProducts.create({ ...payload, id: remoteRecord.id } as never);
        }
      } else {
        const record = editingId ? await localProducts.update(editingId, payload) : await localProducts.create(payload);
        await queueSyncOperation({ entity: 'products', entity_id: record.id, operation: editingId ? 'update' : 'create', payload: record });
      }
      setShowForm(false); setEditingId(null); await loadRows();
    } catch (error) { setMessage(describeError(error, 'Could not save product.')); }
    finally { setBusy(false); }
  }

  async function saveNews(event: FormEvent) {
    event.preventDefault(); setBusy(true); setMessage('');
    try {
      const payload = {
        ...newsForm,
        slug: editingId && newsForm.slug ? newsForm.slug : generateSlug(newsForm.title, 'article'),
        excerpt: newsForm.excerpt || null,
        category: newsForm.category,
        featured_image_url: newsForm.featured_image_url,
        ...(newsForm.gallery_images.length > 0 ? { gallery_images: newsForm.gallery_images } : {}),
        published_at: newsForm.published_at
          ? `${newsForm.published_at}T12:00:00.000Z`
          : newsForm.status === 'published' ? new Date().toISOString() : null,
        meta_title: newsForm.meta_title || null,
        meta_description: newsForm.meta_description || null,
      };
      if (navigator.onLine) {
        const currentUser = await getCurrentUser();
        if (!currentUser) {
          throw new Error('Sign in again before publishing news to the website.');
        }
        const remotePayload = { ...payload, author_id: currentUser.id };
        if (editingId) {
          await db.news.update(editingId, remotePayload);
          await localNews.update(editingId, remotePayload);
        } else {
          const remoteRecord = await db.news.create(remotePayload) as unknown as { id: string };
          await localNews.create({ ...remotePayload, id: remoteRecord.id } as never);
        }
      } else {
        const record = editingId ? await localNews.update(editingId, payload) : await localNews.create(payload);
        await queueSyncOperation({ entity: 'news', entity_id: record.id, operation: editingId ? 'update' : 'create', payload: record });
      }
      setShowForm(false); setEditingId(null); await loadRows();
    } catch (error) {
      const detail = describeError(error, 'Could not save news article.');
      setMessage(detail.includes('gallery_images')
        ? 'News secondary images require the latest database migration. Apply 20260926000023_news_gallery_images.sql in Supabase, then save again.'
        : detail);
    }
    finally { setBusy(false); }
  }

  async function saveGallery(event: FormEvent) {
    event.preventDefault(); setBusy(true); setMessage('');
    try {
      const payload = {
        title: galleryForm.title,
        description: galleryForm.description,
        image_url: galleryForm.image_url,
        thumbnail_url: galleryForm.image_url,
        category: galleryForm.category || 'General',
        sort_order: galleryForm.sort_order,
        is_published: galleryForm.is_published,
      };
      if (navigator.onLine) {
        if (editingId) {
          await db.gallery.update(editingId, payload);
          await localGallery.update(editingId, payload);
        } else {
          const remoteRecord = await db.gallery.create(payload) as unknown as { id: string };
          await localGallery.create({ ...payload, id: remoteRecord.id } as never);
        }
      } else {
        const record = editingId ? await localGallery.update(editingId, payload) : await localGallery.create(payload);
        await queueSyncOperation({ entity: 'gallery', entity_id: record.id, operation: editingId ? 'update' : 'create', payload: record });
      }
      setShowForm(false); setEditingId(null); await loadRows();
    } catch (error) { setMessage(error instanceof Error ? error.message : 'Could not save gallery item.'); }
    finally { setBusy(false); }
  }

  async function savePartner(event: FormEvent) {
    event.preventDefault(); setBusy(true); setMessage('');
    try {
      const payload = { ...partnerForm, description: partnerForm.description || null, logo_url: partnerForm.logo_url || null, website_url: partnerForm.website_url || null };
      if (navigator.onLine) {
        if (editingId) {
          await db.partners.update(editingId, payload);
          await localPartners.update(editingId, payload);
        } else {
          const remoteRecord = await db.partners.create(payload) as unknown as { id: string };
          await localPartners.create({ ...payload, id: remoteRecord.id } as never);
        }
      } else {
        const record = editingId ? await localPartners.update(editingId, payload) : await localPartners.create(payload);
        await queueSyncOperation({ entity: 'partners', entity_id: record.id, operation: editingId ? 'update' : 'create', payload: record });
      }
      setShowForm(false); setEditingId(null); await loadRows();
    } catch (error) { setMessage(error instanceof Error ? error.message : 'Could not save partner.'); }
    finally { setBusy(false); }
  }

  async function saveTeam(event: FormEvent) {
    event.preventDefault(); setBusy(true); setMessage('');
    try {
      const payload = { ...teamForm, bio: teamForm.bio || null, image_url: teamForm.image_url || null, email: teamForm.email || null, phone: teamForm.phone || null, linkedin_url: teamForm.linkedin_url || null };
      if (navigator.onLine) {
        if (editingId) {
          const remoteRecord = await db.team.update(editingId, payload) as unknown as { id: string };
          await localTeamMembers.update(editingId, { ...payload, id: remoteRecord.id } as never);
        } else {
          const created = await db.team.create(payload as never) as unknown as { id: string };
          await localTeamMembers.create({ ...payload, id: created.id } as never);
        }
      } else {
        const record = editingId ? await localTeamMembers.update(editingId, payload) : await localTeamMembers.create(payload);
        await queueSyncOperation({ entity: 'team_members', entity_id: record.id, operation: editingId ? 'update' : 'create', payload: record });
      }
      setShowForm(false); setEditingId(null); await loadRows();
      setMessage('Team member saved successfully.');
    } catch (error) { setMessage(error instanceof Error ? error.message : 'Could not save team member.'); }
    finally { setBusy(false); }
  }

  async function saveUser(event: FormEvent) {
    event.preventDefault(); setBusy(true); setMessage('');
    try {
      if (editingId) {
        if (navigator.onLine) {
          await db.users.updateRole(editingId, userForm.role);
        }
        const record = await localUsers.update(editingId, userForm);
        await queueSyncOperation({ entity: 'users', entity_id: record.id, operation: 'update', payload: record });
        setShowForm(false); setEditingId(null); await loadRows();
        setMessage('User role updated successfully.');
      } else {
        if (!navigator.onLine) {
          throw new Error('You must be online to register new staff accounts.');
        }
        const password = userForm.password || 'Password123!';
        
        // Register user via Supabase Auth with metadata
        const { data: authData, error: authError } = await supabase.auth.signUp({
          email: userForm.email.trim(),
          password,
          options: {
            data: {
              full_name: userForm.name.trim(),
              role: userForm.role,
            },
          },
        });

        if (authError) {
          throw new Error(authError.message);
        }

        const newUserId = authData.user?.id;
        if (newUserId) {
          try {
            await db.users.create({
              id: newUserId,
              email: userForm.email.trim(),
              name: userForm.name.trim(),
              role: userForm.role,
            });
          } catch (dbErr) {
            console.warn('Profile sync:', dbErr);
          }
        }

        await loadRows();
        setShowForm(false);
        setEditingId(null);
        setMessage(`Staff account for "${userForm.name}" created successfully.`);
      }
    } catch (error) { setMessage(describeError(error, 'Could not save user.')); }
    finally { setBusy(false); }
  }

  async function updateStatus(id: string, status: string) {
    setBusy(true); setMessage('Updating status...');
    try {
      const selectedRow = section === 'Messages' ? rows.find((row) => row.id === id) : null;
      const messageIds = selectedRow && Array.isArray(selectedRow.conversation_messages)
        ? (selectedRow.conversation_messages as Array<Record<string, unknown>>).map((row) => String(row.id))
        : [id];

      if (navigator.onLine) {
        if (section === 'Inquiries') {
          await db.inquiries.updateStatus(id, status as InquiryStatus);
        } else if (section === 'Messages') {
          await Promise.all(messageIds.map((messageId) => db.contactMessages.update(messageId, { status })));
        }
      }
      const repository = section === 'Inquiries' ? localInquiries : localMessages;
      if (section === 'Messages') {
        for (const messageId of messageIds) {
          const record = await repository.update(messageId, { status } as never);
          await queueSyncOperation({ entity: 'messages', entity_id: messageId, operation: 'update', payload: record });
        }
      } else {
        const record = await repository.update(id, { status } as never);
        await queueSyncOperation({ entity: 'inquiries', entity_id: id, operation: 'update', payload: record });
      }
      await loadRows();
      setMessage('Status updated successfully.');
    } catch (error) { setMessage(error instanceof Error ? error.message : 'Could not update status.'); }
    finally { setBusy(false); }
  }

  async function updateFeedbackStatus(id: string, status: 'pending' | 'approved' | 'rejected') {
    setBusy(true);
    try {
      await db.feedback.updateStatus(id, status);
      await loadRows();
      setMessage(`Feedback ${status}.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Could not moderate feedback.');
    } finally {
      setBusy(false);
    }
  }

  async function replyToMessage(id: string, replyMessage: string) {
    const trimmedReply = replyMessage.trim();
    if (!trimmedReply) {
      setMessage('Write a reply before sending it.');
      return;
    }

    setBusy(true);
    setMessage('Sending reply...');

    try {
      const currentUser = await getCurrentUser();
      if (!currentUser) {
        throw new Error('Sign in again before replying to a customer message.');
      }

      const payload = {
        status: 'replied',
        reply_message: trimmedReply,
        replied_by: currentUser.id,
        replied_at: new Date().toISOString(),
      };
      const selectedRow = rows.find((row) => row.id === id);
      const messageIds = selectedRow && Array.isArray(selectedRow.conversation_messages)
        ? (selectedRow.conversation_messages as Array<Record<string, unknown>>).map((row) => String(row.id))
        : [id];

      if (navigator.onLine) {
        await db.contactMessages.reply(id, trimmedReply, currentUser.id);
        await Promise.all(messageIds.filter((messageId) => messageId !== id).map((messageId) => db.contactMessages.update(messageId, { status: 'read' })));
      }

      for (const messageId of messageIds) {
        const record = await localMessages.update(messageId, messageId === id ? payload as never : { status: 'read' } as never);
        await queueSyncOperation({ entity: 'messages', entity_id: messageId, operation: 'update', payload: record });
      }
      await loadRows();
      setSelectedDetail((current) => current ? { ...current, ...payload, reply_message: trimmedReply } : current);
      setMessage('Reply sent successfully.');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Could not send the reply.');
    } finally {
      setBusy(false);
    }
  }

  async function closeLiveChat(id: string) {
    await updateStatus(id, 'archived');
    setSelectedDetail((current) => current ? { ...current, status: 'archived' } : current);
  }

  async function updateProductVisibility(id: string, visible: boolean) {
    setBusy(true);
    const nextStatus: Product['status'] = visible ? 'available' : 'draft';
    setMessage(visible ? 'Publishing product on the website...' : 'Pulling product off the website...');
    try {
      const nextPayload = {
        status: nextStatus,
        published_at: visible ? new Date().toISOString() : null,
      };

      if (navigator.onLine) {
        await db.products.update(id, nextPayload);
      }

      const record = await localProducts.update(id, nextPayload as never);
      await queueSyncOperation({ entity: 'products', entity_id: id, operation: 'update', payload: record });
      await loadRows();
      setMessage(visible ? 'Product is now visible on the public website.' : 'Product has been pulled from the public website.');
    } catch (error) {
      setMessage(describeError(error, 'Could not update the product visibility.'));
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string) {
    if (!window.confirm('Are you sure you want to delete this record?')) return;
    setBusy(true);
    try {
      if (navigator.onLine) {
        if (section === 'Inquiries') await db.inquiries.delete(id);
        else if (section === 'Messages') await db.contactMessages.delete(id);
        else if (section === 'Feedback') await db.feedback.delete(id);
        else if (section === 'Products') await db.products.delete(id);
        else if (section === 'News') await db.news.delete(id);
        else if (section === 'Gallery') await db.gallery.delete(id);
        else if (section === 'Partners') await db.partners.delete(id);
        else if (section === 'Team') await db.team.delete(id);
        else if (section === 'Users') {
          // @ts-ignore - Supabase RPC
          const { error: delErr } = await supabase.rpc('delete_staff_user', { target_user_id: id });
          if (delErr) {
            await db.users.delete(id);
          }
        }
      }
    } catch (error) {
      setMessage(describeError(error, 'Could not delete this record from the customer database.'));
      setBusy(false);
      return;
    }
    const repository = section === 'Products' ? localProducts : section === 'News' ? localNews : repositories[section];
    await repository.remove(id);
    await queueSyncOperation({ entity: section === 'Inquiries' ? 'inquiries' : section === 'Messages' ? 'messages' : section.toLowerCase(), entity_id: id, operation: 'delete', payload: null });
    await loadRows();
    setMessage('Record deleted successfully.');
    setBusy(false);
  }

  function toggleSelected(id: string) {
    setSelectedIds((current) => current.includes(id) ? current.filter((value) => value !== id) : [...current, id]);
  }

  function toggleAll() {
    setSelectedIds((current) => current.length === rows.length ? [] : rows.map((row) => String(row.id)));
  }

  async function pushToWebsite() {
    setBusy(true);
    setMessage('Pushing local changes to the customer website...');
    try {
      if (!navigator.onLine) throw new Error('You are offline. Reconnect before pushing to the customer website.');
      const pending = await getSyncQueue();
      const selectedPending = pending.filter((operation) => selectedIds.includes(operation.entity_id));
      if (selectedIds.length === 0) {
        setMessage('Select at least one record before pushing it to the customer website.');
        return;
      }
      if (selectedPending.length === 0) {
        setMessage('The selected records have no pending changes to push. Save or update them first.');
        return;
      }
      const validCategoryId = categories[0]?.id;
      if (validCategoryId) {
        for (const operation of selectedPending) {
          if (operation.entity !== 'products' || !operation.payload || typeof operation.payload !== 'object') continue;
          const payload = operation.payload as Record<string, unknown>;
          if (typeof payload.category_id !== 'string' || !payload.category_id.includes('-')) {
            await localSyncQueue.update(operation.id, { payload: { ...payload, category_id: validCategoryId } });
          }
        }
      }
      const result = await processSyncQueue(createSupabaseSyncAdapter(), (operation) => selectedIds.includes(operation.entity_id));
      setMessage(result.failed === 0 ? `${result.processed} change${result.processed === 1 ? '' : 's'} pushed to the customer website.` : `${result.processed} pushed, ${result.failed} failed. ${result.errors.join(' | ')}`);
      if (result.failed === 0) setSelectedIds([]);
      await loadRows();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Could not push changes to the customer website.');
    } finally {
      setBusy(false);
    }
  }

  const labels: Record<Section, string[]> = { Products: [t('products'), t('newStatus')], News: [t('news'), t('newStatus')], Gallery: [t('gallery'), t('item'), t('publicWebsite')], Partners: [t('partners'), t('item')], Team: [t('team'), t('item'), t('publicWebsite')], Inquiries: [t('inquiries'), t('email'), t('newStatus')], Messages: [t('messages'), t('item'), t('newStatus')], Feedback: ['Feedback', 'Email', 'Status'], Users: [t('users'), t('email'), t('item')] };
  const fields: Record<Section, string[]> = { Products: ['name', 'status'], News: ['title', 'status'], Gallery: ['title', 'category', 'is_published'], Partners: ['name', 'type'], Team: ['name', 'role', 'is_active'], Inquiries: ['customer_name', 'email', 'status'], Messages: ['name', 'subject', 'status'], Feedback: ['author_name', 'author_email', 'status'], Users: ['name', 'email', 'role'] };
  const supportsEditing = section === 'Products' || section === 'News' || section === 'Gallery' || section === 'Partners' || section === 'Team' || section === 'Feedback' || section === 'Users';
  const isReadOnlyQueue = section === 'Inquiries' || section === 'Messages';

  if (section === 'Feedback') {
    return <FeedbackModeration rows={rows} busy={busy} onStatus={(id, status) => void updateFeedbackStatus(id, status)} onDelete={(id) => void remove(id)} />;
  }

  return <div className="legacy-page"><div className="section-titlebar"><div><p className="eyebrow">{t('administration')}</p><h2>{t(section.toLowerCase() as Parameters<typeof t>[0])}</h2><p className="section-titlebar-copy">{section === 'Products' ? (language === 'fr' ? 'Gérez les produits présentés sur votre site public.' : 'Manage the products presented on your public website.') : section === 'News' ? (language === 'fr' ? 'Publiez les actualités et les histoires de votre entreprise.' : 'Publish company news and stories.') : section === 'Gallery' ? (language === 'fr' ? 'Organisez les images présentées à vos visiteurs.' : 'Organize the images shown to your visitors.') : section === 'Partners' ? (language === 'fr' ? 'Gérez les partenaires et les marques affichés publiquement.' : 'Manage the partners and brands shown publicly.') : section === 'Team' ? (language === 'fr' ? 'Présentez les membres de votre équipe.' : 'Present the people behind your organization.') : section === 'Inquiries' ? (language === 'fr' ? 'Suivez les demandes reçues depuis votre site.' : 'Track requests received from your website.') : section === 'Messages' ? (language === 'fr' ? 'Gérez les messages envoyés par vos visiteurs.' : 'Manage messages sent by your visitors.') : (language === 'fr' ? 'Gérez les accès à votre espace de travail.' : 'Manage access to your workspace.')}</p></div><div className="page-actions">{!isReadOnlyQueue && <button className="primary-button titlebar-primary-button" type="button" onClick={(event) => { event.preventDefault(); event.stopPropagation(); beginCreate(); }} aria-expanded={showForm}>+ {t('add')} {section === 'News' ? t('article') : t('item')}</button>}{supportsEditing && <button className="push-button titlebar-push-button" type="button" onClick={() => void pushToWebsite()} disabled={busy || selectedIds.length === 0}>{t('pushSelected')} ({selectedIds.length})</button>}</div></div>{message && !showForm && <p className="editor-message" role="status">{message}</p>}
    {section === 'Products' && <section className="category-manager"><div><p className="eyebrow">{t('catalogSetup')}</p><h3>{t('productCategories')}</h3></div><div className="category-manager-list">{categories.map((category) => <div className="category-manager-row" key={category.id}><span>{category.name}</span><button className="table-action danger" type="button" onClick={() => void deleteCategory(category)} disabled={busy}>{t('delete')}</button></div>)}</div></section>}
    {showForm && supportsEditing && section === 'Products' && <div className="currency-control"><label>Currency<select value={productForm.currency} onChange={(event) => setProductForm((current) => ({ ...current, currency: event.target.value as Currency }))}><option value="USD">USD - US Dollar</option><option value="EUR">EUR - Euro</option><option value="GBP">GBP - Pound Sterling</option><option value="CAD">CAD - Canadian Dollar</option><option value="AUD">AUD - Australian Dollar</option><option value="DZD">DZD - Algerian Dinar</option><option value="MAD">MAD - Moroccan Dirham</option><option value="XOF">XOF - West African CFA Franc</option><option value="GNF">GNF - Guinean Franc</option></select></label></div>}
    {showForm && supportsEditing && section === 'Products' && <ProductEditor form={productForm} setForm={setProductForm} categories={categories} categoriesLoading={categoriesLoading} refreshCategories={() => void loadCategories()} showCategoryForm={showCategoryForm} setShowCategoryForm={setShowCategoryForm} newCategoryName={newCategoryName} setNewCategoryName={setNewCategoryName} onCreateCategory={createCategory} busy={busy} message={message} onSubmit={saveProduct} onUpload={(event) => void handleImageUpload(event, 'product')} onGalleryUpload={(event) => void handleImageUpload(event, 'product', true)} onCancel={() => setShowForm(false)} editing={Boolean(editingId)} />}
    {showForm && supportsEditing && section === 'News' && <NewsEditor form={newsForm} setForm={setNewsForm} busy={busy} message={message} onSubmit={saveNews} onUpload={(event) => void handleImageUpload(event, 'news')} onGalleryUpload={(event) => void handleImageUpload(event, 'news', true)} onCancel={() => setShowForm(false)} editing={Boolean(editingId)} />}
    {showForm && supportsEditing && section === 'Gallery' && <GalleryEditor form={galleryForm} setForm={setGalleryForm} busy={busy} message={message} onSubmit={saveGallery} onUpload={(event) => void handleImageUpload(event, 'gallery')} onCancel={() => setShowForm(false)} editing={Boolean(editingId)} />}
    {showForm && supportsEditing && section === 'Partners' && <PartnerEditor form={partnerForm} setForm={setPartnerForm} busy={busy} message={message} onSubmit={savePartner} onUpload={(event) => void handleImageUpload(event, 'partner')} onCancel={() => setShowForm(false)} editing={Boolean(editingId)} />}
    {showForm && section === 'Team' && <TeamEditor form={teamForm} setForm={setTeamForm} busy={busy} message={message} onSubmit={saveTeam} onUpload={(event) => void handleImageUpload(event, 'team')} onCancel={() => setShowForm(false)} editing={Boolean(editingId)} />}
    {showForm && section === 'Users' && <UserEditor form={userForm} setForm={setUserForm} busy={busy} message={message} onSubmit={saveUser} onCancel={() => setShowForm(false)} editing={Boolean(editingId)} />}
    <section className="legacy-table-panel"><table className="legacy-table"><thead><tr>{supportsEditing && <th><input type="checkbox" aria-label="Select all records" checked={rows.length > 0 && selectedIds.length === rows.length} onChange={toggleAll} /></th>}<th>ID</th>{labels[section].map((label) => <th key={label}>{label}</th>)}<th>Actions</th></tr></thead><tbody>{rows.length === 0 ? <tr><td colSpan={labels[section].length + (supportsEditing ? 3 : 2)}><div className="empty-state"><strong>No {section.toLowerCase()} records yet</strong><span>{isReadOnlyQueue ? 'New submissions will appear here as they come in.' : supportsEditing ? `Create your first ${section === 'News' ? 'article' : section === 'Gallery' ? 'gallery image' : section === 'Team' ? 'team member' : section === 'Users' ? 'user' : 'product'} above.` : 'Local records will appear here when created or synchronized.'}</span></div></td></tr> : rows.map((row, index) => <tr key={String(row.id)}>{supportsEditing && <td><input type="checkbox" aria-label={`Select ${String(row.name ?? row.title ?? row.customer_name ?? 'record')}`} checked={selectedIds.includes(String(row.id))} onChange={() => toggleSelected(String(row.id))} /></td>}<td>{index + 1}</td>{fields[section].map((field) => <td key={field}>{field === 'status' && (section === 'Inquiries' || section === 'Messages') ? <select value={String(row[field] ?? '')} onChange={(event) => void updateStatus(String(row.id), event.target.value)}><option value={section === 'Inquiries' ? 'new' : 'unread'}>{section === 'Inquiries' ? 'New' : 'Unread'}</option>{section === 'Inquiries' ? <><option value="contacted">Contacted</option><option value="quoted">Quoted</option><option value="closed">Closed</option><option value="spam">Spam</option></> : <><option value="read">Read</option><option value="replied">Replied</option><option value="archived">Archived</option></>}</select> : section === 'Products' && field === 'status' ? <select value={String(row.status ?? 'draft')} onChange={(event) => void updateProductVisibility(String(row.id), event.target.value === 'available')}><option value="draft">Hidden</option><option value="available">Visible</option><option value="out_of_stock">Out of stock</option><option value="discontinued">Discontinued</option></select> : section === 'Team' && field === 'name' ? <div className="table-member-cell">{row.image_url ? <img className="table-member-avatar" src={String(row.image_url)} alt={String(row.name)} /> : <div className="table-member-fallback">{String(row.name || 'T').charAt(0).toUpperCase()}</div>}<strong>{String(row.name ?? '-')}</strong></div> : section === 'Team' && field === 'is_active' ? <span className={`badge-visibility ${row.is_active ? 'active' : 'inactive'}`}>{row.is_active ? '✓ Public Website' : '🔒 Internal only'}</span> : section === 'Users' && field === 'name' ? <div className="table-member-cell"><div className="table-member-fallback" style={{ background: '#dbeafe', color: '#1d4ed8' }}>{String(row.name || row.email || 'U').charAt(0).toUpperCase()}</div><strong>{String(row.name || 'User')}</strong></div> : section === 'Users' && field === 'role' ? <span className="badge-visibility active" style={{ background: '#e0e7ff', color: '#4338ca', textTransform: 'capitalize' }}>{String(row.role || 'viewer').replace('_', ' ')}</span> : <span>{String(row[field] ?? '-')}</span>}</td>)}<td>{supportsEditing && <button className="table-action" type="button" onClick={() => beginEdit(row)}>Edit</button>}{(section === 'Inquiries' || section === 'Messages' || section === 'Team' || section === 'Users') && <button className="table-action" type="button" onClick={() => setSelectedDetail(row)}>View</button>}{section === 'Products' ? <button className="table-action" type="button" onClick={() => void updateProductVisibility(String(row.id), String(row.status ?? 'draft') !== 'available')} disabled={busy}>{String(row.status ?? 'draft') === 'available' ? 'Pull from website' : 'Publish to website'}</button> : null}<button className="table-action danger" type="button" onClick={() => void remove(String(row.id))}>Delete</button></td></tr>)}</tbody></table></section>
    {selectedDetail && <DetailModal item={selectedDetail} conversationMessages={Array.isArray(selectedDetail.conversation_messages) ? selectedDetail.conversation_messages as Array<Record<string, unknown>> : rows.filter((row) => row.conversation_id && row.conversation_id === selectedDetail.conversation_id)} section={section} busy={busy} onClose={closeDetail} onUpdateStatus={(status) => { void updateStatus(String(selectedDetail.id), status); updateSelectedDetailStatus(status); }} onReply={(reply) => { void replyToMessage(String(selectedDetail.id), reply); }} onCloseChat={() => { void closeLiveChat(String(selectedDetail.id)); }} onEdit={(item) => beginEdit(item)} />}
  </div>;
}

function FeedbackModeration({ rows, busy, onStatus, onDelete }: { rows: Array<Record<string, unknown>>; busy: boolean; onStatus: (id: string, status: 'pending' | 'approved' | 'rejected') => void; onDelete: (id: string) => void }) {
  const [selectedFeedback, setSelectedFeedback] = useState<Record<string, unknown> | null>(null);
  return <div className="legacy-page"><div className="section-titlebar"><div><p className="eyebrow">Administration</p><h2>Feedback</h2><p className="section-titlebar-copy">Review visitor feedback before it appears publicly.</p></div></div><section className="legacy-table-panel"><table className="legacy-table"><thead><tr><th>Author</th><th>Email</th><th>Type</th><th>Feedback</th><th>Status</th><th>Actions</th></tr></thead><tbody>{rows.length === 0 ? <tr><td colSpan={6}><div className="empty-state"><strong>No feedback yet</strong><span>Visitor feedback submissions will appear here.</span></div></td></tr> : rows.map((row) => <tr key={String(row.id)}><td>{String(row.author_name ?? '')}</td><td>{String(row.author_email ?? '')}</td><td>{String(row.content_type ?? '')}</td><td className="feedback-table-comment">{String(row.comment ?? '')}</td><td><select value={String(row.status ?? 'pending')} onChange={(event) => onStatus(String(row.id), event.target.value as 'pending' | 'approved' | 'rejected')} disabled={busy}><option value="pending">Pending</option><option value="approved">Approved</option><option value="rejected">Rejected</option></select></td><td><button className="table-action" type="button" onClick={() => setSelectedFeedback(row)}>View</button><button className="table-action danger" type="button" onClick={() => onDelete(String(row.id))} disabled={busy}>Delete</button></td></tr>)}</tbody></table></section>{selectedFeedback && <div className="detail-modal-overlay" onClick={() => setSelectedFeedback(null)} role="dialog" aria-modal="true"><div className="detail-modal" onClick={(event) => event.stopPropagation()}><div className="detail-modal-header"><div><p className="eyebrow">Visitor feedback</p><h3>{String(selectedFeedback.author_name ?? 'Anonymous')}</h3></div><button className="secondary-button" type="button" onClick={() => setSelectedFeedback(null)}>Close</button></div><div className="detail-modal-body"><div className="detail-grid"><div className="detail-item"><span className="detail-label">Email</span><span className="detail-value">{String(selectedFeedback.author_email ?? '')}</span></div><div className="detail-item"><span className="detail-label">Content type</span><span className="detail-value">{String(selectedFeedback.content_type ?? '')}</span></div><div className="detail-item"><span className="detail-label">Rating</span><span className="detail-value">{selectedFeedback.rating ? `${String(selectedFeedback.rating)} / 5` : 'No rating'}</span></div><div className="detail-item"><span className="detail-label">Status</span><span className="detail-value">{String(selectedFeedback.status ?? 'pending')}</span></div></div><div className="detail-item"><span className="detail-label">Feedback</span><div className="detail-message-box">{String(selectedFeedback.comment ?? '')}</div></div></div><div className="detail-modal-footer"><button className="secondary-button" type="button" onClick={() => onStatus(String(selectedFeedback.id), 'rejected')}>Reject</button><button className="primary-button" type="button" onClick={() => onStatus(String(selectedFeedback.id), 'approved')}>Approve</button></div></div></div>}</div>;
}

function ProductEditor({ form, setForm, categories, categoriesLoading, refreshCategories, showCategoryForm, setShowCategoryForm, newCategoryName, setNewCategoryName, onCreateCategory, busy, message, onSubmit, onUpload, onGalleryUpload, onCancel, editing }: { form: ProductForm; setForm: (value: ProductForm | ((current: ProductForm) => ProductForm)) => void; categories: Array<{ id: string; name: string }>; categoriesLoading: boolean; refreshCategories: () => void; showCategoryForm: boolean; setShowCategoryForm: (value: boolean) => void; newCategoryName: string; setNewCategoryName: (value: string) => void; onCreateCategory: (event: FormEvent) => void; busy: boolean; message: string; onSubmit: (event: FormEvent) => void; onUpload: (event: ChangeEvent<HTMLInputElement>) => void; onGalleryUpload: (event: ChangeEvent<HTMLInputElement>) => void; onCancel: () => void; editing: boolean }) {
  return <form className="content-editor" onSubmit={onSubmit}><div className="editor-heading"><div><p className="eyebrow">Catalog content</p><h3>{editing ? 'Edit product' : 'Add product'}</h3></div><button className="secondary-button" type="button" onClick={onCancel}>Cancel</button></div><div className="editor-grid"><label>Name<input required value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value, slug: current.slug === slugify(current.name) || !current.slug ? slugify(event.target.value) : current.slug }))} /></label><div className="category-field"><label>Category<select required value={form.category_id} onChange={(event) => setForm({ ...form, category_id: event.target.value })}><option value="">{categoriesLoading ? 'Loading categories...' : categories.length === 0 ? 'No categories available' : 'Select a category'}</option>{categories.map((category) => <option key={category.id} value={category.id}>{category.name}</option>)}</select></label><div className="category-actions"><button className="category-toggle" type="button" onClick={refreshCategories} disabled={categoriesLoading}>{categoriesLoading ? 'Loading...' : `Refresh categories (${categories.length})`}</button><button className="category-toggle" type="button" onClick={() => setShowCategoryForm(!showCategoryForm)}>+ Add category</button></div>{showCategoryForm && <div className="category-creator"><input aria-label="New category name" placeholder="New category name" value={newCategoryName} onChange={(event) => setNewCategoryName(event.target.value)} /><button className="secondary-button" type="button" onClick={(event) => onCreateCategory(event as unknown as FormEvent)}>Create</button></div>}</div><label>Price (optional)<input type="number" min="0" step="0.01" value={form.price ?? ''} onChange={(event) => setForm({ ...form, price: event.target.value === '' ? null : Number(event.target.value) })} placeholder="Leave empty for quote" /></label><label>Status<select value={form.status} onChange={(event) => setForm({ ...form, status: event.target.value as Product['status'] })}><option value="draft">Draft</option><option value="available">Available</option><option value="out_of_stock">Out of stock</option><option value="discontinued">Discontinued</option></select></label><label className="wide-field">Short description<input value={form.short_description} onChange={(event) => setForm({ ...form, short_description: event.target.value })} /></label><label className="wide-field">Description<textarea required value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} /></label><label>Product image<input type="file" accept="image/*" onChange={onUpload} disabled={busy} /></label><label>Additional images<input type="file" accept="image/*" multiple onChange={onGalleryUpload} disabled={busy} /></label><label className="checkbox-field"><input type="checkbox" checked={form.featured} onChange={(event) => setForm({ ...form, featured: event.target.checked })} /> Featured product</label></div>{form.image_url && <img className="editor-image-preview" src={form.image_url} alt="Product preview" />} {form.gallery_images.length > 0 && <div className="editor-gallery-preview">{form.gallery_images.map((image) => <img key={image} src={image} alt="Product detail" />)}</div>} {message && <p className="editor-message">{message}</p>}<button className="primary-button" type="submit" disabled={busy || categories.length === 0}>{busy ? 'Saving...' : editing ? 'Update product' : 'Save product'}</button></form>;
}

function NewsEditor({ form, setForm, busy, message, onSubmit, onUpload, onGalleryUpload, onCancel, editing }: { form: NewsForm; setForm: (value: NewsForm | ((current: NewsForm) => NewsForm)) => void; busy: boolean; message: string; onSubmit: (event: FormEvent) => void; onUpload: (event: ChangeEvent<HTMLInputElement>) => void; onGalleryUpload: (event: ChangeEvent<HTMLInputElement>) => void; onCancel: () => void; editing: boolean }) {
  return <form className="content-editor" onSubmit={onSubmit}><div className="editor-heading"><div><p className="eyebrow">Public website content</p><h3>{editing ? 'Edit news article' : 'Add news article'}</h3></div><button className="secondary-button" type="button" onClick={onCancel}>Cancel</button></div><div className="editor-grid"><label>Title<input required value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} /></label><label>Status<select value={form.status} onChange={(event) => setForm({ ...form, status: event.target.value as News['status'] })}><option value="draft">Draft</option><option value="published">Published</option><option value="archived">Archived</option></select></label><label>Category<select value={form.category} onChange={(event) => setForm({ ...form, category: event.target.value as NewsForm['category'] })}><option value="company">Company</option><option value="industry">Industry</option><option value="launches">Launches</option><option value="events">Events</option><option value="tips">Tips</option></select></label><label>Publication date<input type="date" value={form.published_at ?? ''} onChange={(event) => setForm({ ...form, published_at: event.target.value || null })} /></label><label>Featured image<input type="file" accept="image/*" onChange={onUpload} disabled={busy} /></label><label className="wide-field">Excerpt<input value={form.excerpt ?? ''} onChange={(event) => setForm({ ...form, excerpt: event.target.value || null })} /></label><label className="wide-field">Article content<textarea required value={form.content} onChange={(event) => setForm({ ...form, content: event.target.value })} /></label><label className="checkbox-field"><input type="checkbox" checked={form.featured} onChange={(event) => setForm({ ...form, featured: event.target.checked })} /> Featured article</label><label className="wide-field">Secondary images<input type="file" accept="image/*" multiple onChange={onGalleryUpload} disabled={busy} /></label></div>{form.featured_image_url && <img className="editor-image-preview" src={form.featured_image_url} alt="Article preview" />} {form.gallery_images.length > 0 && <div className="editor-gallery-preview">{form.gallery_images.map((image, index) => <img key={`${image}-${index}`} src={image} alt={`Article image ${index + 1}`} />)}</div>} {message && <p className="editor-message">{message}</p>}<button className="primary-button" type="submit" disabled={busy}>{busy ? 'Saving...' : editing ? 'Update article' : 'Save article'}</button></form>;
}

function GalleryEditor({ form, setForm, busy, message, onSubmit, onUpload, onCancel, editing }: { form: GalleryForm; setForm: (value: GalleryForm | ((current: GalleryForm) => GalleryForm)) => void; busy: boolean; message: string; onSubmit: (event: FormEvent) => void; onUpload: (event: ChangeEvent<HTMLInputElement>) => void; onCancel: () => void; editing: boolean }) {
  return <form className="content-editor" onSubmit={onSubmit}><div className="editor-heading"><div><p className="eyebrow">Gallery content</p><h3>{editing ? 'Edit gallery item' : 'Add gallery item'}</h3></div><button className="secondary-button" type="button" onClick={onCancel}>Cancel</button></div><div className="editor-grid"><label>Title<input required value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} /></label><label>Category<input value={form.category ?? ''} onChange={(event) => setForm({ ...form, category: event.target.value || null })} /></label><label>Sort order<input type="number" min="0" value={form.sort_order} onChange={(event) => setForm({ ...form, sort_order: Number(event.target.value) || 0 })} /></label><label>Image<input type="file" accept="image/*" onChange={onUpload} disabled={busy} /></label><label className="checkbox-field"><input type="checkbox" checked={form.is_published} onChange={(event) => setForm({ ...form, is_published: event.target.checked })} /> Published</label><label className="wide-field">Description<textarea value={form.description ?? ''} onChange={(event) => setForm({ ...form, description: event.target.value || null })} /></label></div>{form.image_url && <img className="editor-image-preview" src={form.image_url} alt="Gallery preview" />} {message && <p className="editor-message">{message}</p>}<button className="primary-button" type="submit" disabled={busy}>{busy ? 'Saving...' : editing ? 'Update gallery item' : 'Save gallery item'}</button></form>;
}

function PartnerEditor({ form, setForm, busy, message, onSubmit, onUpload, onCancel, editing }: { form: PartnerForm; setForm: (value: PartnerForm | ((current: PartnerForm) => PartnerForm)) => void; busy: boolean; message: string; onSubmit: (event: FormEvent) => void; onUpload: (event: ChangeEvent<HTMLInputElement>) => void; onCancel: () => void; editing: boolean }) {
  return <form className="content-editor" onSubmit={onSubmit}><div className="editor-heading"><div><p className="eyebrow">Partner directory</p><h3>{editing ? 'Edit partner' : 'Add partner'}</h3></div><button className="secondary-button" type="button" onClick={onCancel}>Cancel</button></div><div className="editor-grid"><label>Name<input required value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} /></label><label>Type<select value={form.type} onChange={(event) => setForm({ ...form, type: event.target.value as Partner['type'] })}><option value="national">National</option><option value="international">International</option></select></label><label>Logo image<input type="file" accept="image/*" onChange={onUpload} disabled={busy} /></label><label>Logo URL<input type="url" value={form.logo_url ?? ''} onChange={(event) => setForm({ ...form, logo_url: event.target.value || null })} /></label><label>Website URL<input type="url" value={form.website_url ?? ''} onChange={(event) => setForm({ ...form, website_url: event.target.value || null })} /></label><label>Sort order<input type="number" min="0" value={form.sort_order} onChange={(event) => setForm({ ...form, sort_order: Number(event.target.value) || 0 })} /></label><label className="checkbox-field"><input type="checkbox" checked={form.is_active} onChange={(event) => setForm({ ...form, is_active: event.target.checked })} /> Active on public website</label><label className="wide-field">Description<textarea value={form.description ?? ''} onChange={(event) => setForm({ ...form, description: event.target.value || null })} /></label></div>{form.logo_url && <img className="editor-image-preview" src={form.logo_url} alt="Partner logo preview" />} {message && <p className="editor-message">{message}</p>}<button className="primary-button" type="submit" disabled={busy}>{busy ? 'Saving...' : editing ? 'Update partner' : 'Save partner'}</button></form>;
}

function TeamEditor({ form, setForm, busy, message, onSubmit, onUpload, onCancel, editing }: { form: TeamForm; setForm: (value: TeamForm | ((current: TeamForm) => TeamForm)) => void; busy: boolean; message: string; onSubmit: (event: FormEvent) => void; onUpload: (event: ChangeEvent<HTMLInputElement>) => void; onCancel: () => void; editing: boolean }) {
  return <form className="content-editor" onSubmit={onSubmit}><div className="editor-heading"><div><p className="eyebrow">Team members</p><h3>{editing ? 'Edit team member' : 'Add team member'}</h3></div><button className="secondary-button" type="button" onClick={onCancel}>Cancel</button></div><div className="editor-grid"><label>Name<input required value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} /></label><label>Role<input required value={form.role} onChange={(event) => setForm({ ...form, role: event.target.value })} /></label><label>Email<input type="email" value={form.email ?? ''} onChange={(event) => setForm({ ...form, email: event.target.value || null })} /></label><label>Phone<input type="tel" value={form.phone ?? ''} onChange={(event) => setForm({ ...form, phone: event.target.value || null })} /></label><label>LinkedIn URL<input type="url" value={form.linkedin_url ?? ''} onChange={(event) => setForm({ ...form, linkedin_url: event.target.value || null })} /></label><label>Photo<input type="file" accept="image/*" onChange={onUpload} disabled={busy} /></label><label>Sort order<input type="number" min="0" value={form.sort_order} onChange={(event) => setForm({ ...form, sort_order: Number(event.target.value) || 0 })} /></label><label className="checkbox-field"><input type="checkbox" checked={form.is_active} onChange={(event) => setForm({ ...form, is_active: event.target.checked })} /> Active on public website</label><label className="wide-field">Bio<textarea value={form.bio ?? ''} onChange={(event) => setForm({ ...form, bio: event.target.value || null })} /></label></div>{form.image_url && <img className="editor-image-preview" src={form.image_url} alt="Team member preview" />} {message && <p className="editor-message">{message}</p>}<button className="primary-button" type="submit" disabled={busy}>{busy ? 'Saving...' : editing ? 'Update team member' : 'Save team member'}</button></form>;
}

function UserEditor({ form, setForm, busy, message, onSubmit, onCancel, editing }: { form: UserForm; setForm: (value: UserForm | ((current: UserForm) => UserForm)) => void; busy: boolean; message: string; onSubmit: (event: FormEvent) => void; onCancel: () => void; editing: boolean }) {
  return <form className="content-editor" onSubmit={onSubmit}><div className="editor-heading"><div><p className="eyebrow">Staff access control</p><h3>{editing ? 'Edit user role' : 'Add staff user'}</h3></div><button className="secondary-button" type="button" onClick={onCancel}>Cancel</button></div><div className="editor-grid"><label>Email<input required type="email" disabled={editing} value={form.email} onChange={(event) => setForm((prev) => ({ ...prev, email: event.target.value }))} /></label><label>Name<input required value={form.name} onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))} /></label><label>Role<select value={form.role} onChange={(event) => setForm((prev) => ({ ...prev, role: event.target.value as User['role'] }))}><option value="viewer">Viewer</option><option value="content_manager">Content Manager</option><option value="manager">Manager</option><option value="super_admin">Super Admin</option></select></label>{!editing && <label>Initial Password (min 6 characters)<input type="password" required minLength={6} placeholder="e.g. Password123!" value={form.password ?? ''} onChange={(event) => setForm((prev) => ({ ...prev, password: event.target.value }))} /></label>}</div>{!editing && <div style={{ padding: '10px 14px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '6px', fontSize: '13px', color: '#166534' }}>💡 Staff accounts created here are activated immediately and can log into the admin dashboard with their assigned credentials.</div>}{message && <p className="editor-message">{message}</p>}<button className="primary-button" type="submit" disabled={busy}>{busy ? 'Saving...' : editing ? 'Update role' : 'Create user'}</button></form>;
}

function DetailModal({
  item,
  conversationMessages,
  section,
  busy,
  onClose,
  onUpdateStatus,
  onReply,
  onCloseChat,
  onEdit,
}: {
  item: Record<string, unknown> | null;
  conversationMessages?: Array<Record<string, unknown>>;
  section: Section;
  busy?: boolean;
  onClose: () => void;
  onUpdateStatus: (status: string) => void;
  onReply?: (reply: string) => void;
  onCloseChat?: () => void;
  onEdit?: (item: Record<string, unknown>) => void;
}) {
  const [replyDraft, setReplyDraft] = useState('');
  useEffect(() => {
    if (item) setReplyDraft(String(item.reply_message ?? ''));
  }, [item?.id, item?.reply_message]);

  if (!item) return null;

  if (section === 'Team') {
    const name = String(item.name ?? 'Team Member');
    const role = String(item.role ?? '');
    const email = String(item.email ?? '');
    const phone = String(item.phone ?? '');
    const linkedin = String(item.linkedin_url ?? '');
    const imageUrl = item.image_url ? String(item.image_url) : null;
    const bio = String(item.bio ?? 'No biography provided.');
    const isActive = Boolean(item.is_active);
    const sortOrder = Number(item.sort_order ?? 0);

    return (
      <div className="detail-modal-overlay" onClick={onClose} role="dialog" aria-modal="true">
        <div className="detail-modal" onClick={(e) => e.stopPropagation()}>
          <div className="detail-modal-header">
            <div>
              <p className="eyebrow">Team Member Profile</p>
              <h3>{name}</h3>
            </div>
            <button className="secondary-button" type="button" onClick={onClose}>
              ✕ Close
            </button>
          </div>
          <div className="detail-modal-body">
            <div className="team-profile-header">
              {imageUrl ? (
                <img className="team-profile-photo" src={imageUrl} alt={name} />
              ) : (
                <div className="team-profile-fallback">{name.charAt(0).toUpperCase()}</div>
              )}
              <div className="team-profile-meta">
                <h4>{name}</h4>
                <span>{role}</span>
                <div style={{ marginTop: '6px' }}>
                  <span className={`badge-visibility ${isActive ? 'active' : 'inactive'}`}>
                    {isActive ? '✓ Public Website (Visible)' : '🔒 Internal Workspace (Not on website)'}
                  </span>
                </div>
              </div>
            </div>

            <div className="detail-grid">
              <div className="detail-item">
                <span className="detail-label">Email</span>
                <span className="detail-value">
                  {email ? <a href={`mailto:${email}`}>{email}</a> : 'N/A'}
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Phone</span>
                <span className="detail-value">
                  {phone ? <a href={`tel:${phone}`}>{phone}</a> : 'N/A'}
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-label">LinkedIn Profile</span>
                <span className="detail-value">
                  {linkedin ? (
                    <a href={linkedin} target="_blank" rel="noopener noreferrer">
                      View LinkedIn Profile ↗
                    </a>
                  ) : (
                    'N/A'
                  )}
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Display Order</span>
                <span className="detail-value">{sortOrder}</span>
              </div>
            </div>

            <div className="detail-item">
              <span className="detail-label">Biography & Information</span>
              <div className="detail-message-box">{bio}</div>
            </div>
          </div>
          <div className="detail-modal-footer">
            {onEdit && (
              <button
                className="primary-button"
                type="button"
                onClick={() => {
                  onClose();
                  onEdit(item);
                }}
              >
                Edit Member
              </button>
            )}
            <button className="secondary-button" type="button" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (section === 'Users') {
    const name = String(item.name ?? 'Staff User');
    const email = String(item.email ?? '');
    const role = String(item.role ?? 'viewer');
    const createdAt = item.created_at ? new Date(String(item.created_at)).toLocaleString() : 'N/A';
    const lastSignIn = item.last_sign_in_at ? new Date(String(item.last_sign_in_at)).toLocaleString() : 'Never / Not recorded';

    return (
      <div className="detail-modal-overlay" onClick={onClose} role="dialog" aria-modal="true">
        <div className="detail-modal" onClick={(e) => e.stopPropagation()}>
          <div className="detail-modal-header">
            <div>
              <p className="eyebrow">Staff Account</p>
              <h3>{name}</h3>
            </div>
            <button className="secondary-button" type="button" onClick={onClose}>
              ✕ Close
            </button>
          </div>
          <div className="detail-modal-body">
            <div className="team-profile-header">
              <div className="team-profile-fallback" style={{ background: '#dbeafe', color: '#1d4ed8' }}>
                {name.charAt(0).toUpperCase()}
              </div>
              <div className="team-profile-meta">
                <h4>{name}</h4>
                <span style={{ textTransform: 'capitalize' }}>{role.replace('_', ' ')}</span>
                <div style={{ marginTop: '6px' }}>
                  <span className="badge-visibility active" style={{ background: '#e0e7ff', color: '#4338ca' }}>
                    Access Level: {role.replace('_', ' ')}
                  </span>
                </div>
              </div>
            </div>

            <div className="detail-grid">
              <div className="detail-item">
                <span className="detail-label">Email Address</span>
                <span className="detail-value">
                  {email ? <a href={`mailto:${email}`}>{email}</a> : 'N/A'}
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Account Created</span>
                <span className="detail-value">{createdAt}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Assigned Role</span>
                <span className="detail-value" style={{ textTransform: 'capitalize', fontWeight: 600 }}>
                  {role.replace('_', ' ')}
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Last Sign In</span>
                <span className="detail-value">{lastSignIn}</span>
              </div>
            </div>
          </div>
          <div className="detail-modal-footer">
            {onEdit && (
              <button
                className="primary-button"
                type="button"
                onClick={() => {
                  onClose();
                  onEdit(item);
                }}
              >
                Edit Role / User
              </button>
            )}
            <button className="secondary-button" type="button" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  const name = String(item.customer_name ?? item.name ?? 'Anonymous');
  const email = String(item.email ?? '');
  const phone = String(item.phone ?? '');
  const subject = String(item.subject ?? 'No Subject');
  const message = String(item.message ?? 'No content');
  const createdAt = item.created_at ? new Date(String(item.created_at)).toLocaleString() : 'N/A';
  const status = String(item.status ?? '');

  return (
    <div className="detail-modal-overlay" onClick={onClose} role="dialog" aria-modal="true">
      <div className="detail-modal" onClick={(e) => e.stopPropagation()}>
        <div className="detail-modal-header">
          <div>
            <p className="eyebrow">{section === 'Inquiries' ? 'Inquiry Details' : 'Message Details'}</p>
            <h3>{subject}</h3>
          </div>
          <button className="secondary-button" type="button" onClick={onClose}>
            ✕ Close
          </button>
        </div>
        <div className="detail-modal-body">
          <div className="detail-grid">
            <div className="detail-item">
              <span className="detail-label">From</span>
              <span className="detail-value"><strong>{name}</strong></span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Date Received</span>
              <span className="detail-value">{createdAt}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Email</span>
              <span className="detail-value">
                {email ? <a href={`mailto:${email}`}>{email}</a> : 'N/A'}
              </span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Phone</span>
              <span className="detail-value">
                {phone && phone !== 'N/A' ? <a href={`tel:${phone}`}>{phone}</a> : 'N/A'}
              </span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Status</span>
              <div className="detail-value">
                <select
                  value={status}
                  onChange={(e) => onUpdateStatus(e.target.value)}
                  style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #ceddce', marginTop: '4px' }}
                >
                  {section === 'Inquiries' ? (
                    <>
                      <option value="new">New</option>
                      <option value="contacted">Contacted</option>
                      <option value="quoted">Quoted</option>
                      <option value="closed">Closed</option>
                      <option value="spam">Spam</option>
                    </>
                  ) : (
                    <>
                      <option value="unread">Unread</option>
                      <option value="read">Read</option>
                      <option value="replied">Replied</option>
                      <option value="archived">Archived</option>
                    </>
                  )}
                </select>
              </div>
            </div>
          </div>
          <div className="detail-item">
            <span className="detail-label">Message Content</span>
            <div className="detail-message-box">{message}</div>
          </div>

          {section === 'Messages' && conversationMessages && conversationMessages.length > 1 && (
            <div className="detail-item">
              <span className="detail-label">Conversation</span>
              <div className="admin-chat-thread">
                {[...conversationMessages].sort((left, right) => String(left.created_at ?? '').localeCompare(String(right.created_at ?? ''))).map((conversationMessage) => (
                  <div className="admin-chat-message" key={String(conversationMessage.id)}>
                    <span className="admin-chat-message-meta">{String(conversationMessage.name ?? name)}</span>
                    <p>{String(conversationMessage.message ?? '')}</p>
                    <time>{conversationMessage.created_at ? new Date(String(conversationMessage.created_at)).toLocaleString() : ''}</time>
                    {Boolean(conversationMessage.reply_message) && <><span className="admin-chat-message-meta admin-chat-reply-meta">SIAEDTS reply</span><p>{String(conversationMessage.reply_message)}</p></>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {section === 'Messages' && (
            <div className="detail-item">
              <span className="detail-label">Reply to Customer</span>
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  if (onReply) onReply(replyDraft);
                }}
                style={{ display: 'grid', gap: '10px' }}
              >
                <textarea
                  value={replyDraft}
                  rows={4}
                  placeholder="Write a friendly response to the customer..."
                  onChange={(event) => setReplyDraft(event.target.value)}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #ceddce', resize: 'vertical' }}
                />
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                  <button className="secondary-button" type="button" onClick={onClose}>
                    Close
                  </button>
                  <button className="primary-button" type="submit" disabled={busy || !replyDraft.trim()}>
                    {busy ? 'Sending...' : 'Send reply'}
                  </button>
                  {onCloseChat && <button className="secondary-button close-chat-button" type="button" onClick={onCloseChat} disabled={busy}>Close live chat</button>}
                </div>
              </form>
            </div>
          )}
        </div>
        {section !== 'Messages' && (
          <div className="detail-modal-footer">
            {email && (
              <a href={`mailto:${email}?subject=Re: ${encodeURIComponent(subject)}`} className="primary-button" style={{ textDecoration: 'none' }}>
                Reply via Email
              </a>
            )}
            <button className="secondary-button" type="button" onClick={onClose}>
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
